import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface UnsavedChangesLogoutDialogProps {
  open: boolean
  isSaving: boolean
  isDeleting?: boolean
  onSaveDraftAndLogout: () => void
  onLogoutWithoutSaving: () => void
  onCancel: () => void
}

export function UnsavedChangesLogoutDialog({
  open,
  isSaving,
  isDeleting = false,
  onSaveDraftAndLogout,
  onLogoutWithoutSaving,
  onCancel,
}: UnsavedChangesLogoutDialogProps) {
  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 z-[400] bg-black/40 backdrop-blur-[2px]"
        onClick={onCancel}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-logout-title"
        className={cn(
          'fixed z-[401] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-sm bg-[var(--surface)] rounded-[var(--radius)]',
          'border border-[var(--border)] shadow-2xl animate-fade-in',
          'p-6'
        )}
      >
        <h2
          id="unsaved-logout-title"
          className="text-[15px] font-bold text-[var(--text)] mb-2"
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {getMessage('header.unsaved_changes_title')}
        </h2>

        <p className="text-[12px] text-[var(--text-dim)] font-mono leading-relaxed mb-6">
          {getMessage('header.unsaved_changes_message')}
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={onSaveDraftAndLogout}
            disabled={isSaving || isDeleting}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-5 py-[9px] rounded-[var(--radius-sm)]',
              'text-white text-[12px] font-medium border border-transparent',
              'transition-all duration-[180ms] hover:opacity-90 cursor-pointer',
              (isSaving || isDeleting) && 'opacity-70 cursor-not-allowed'
            )}
            style={{ background: 'linear-gradient(135deg, var(--accent), #2563eb)' }}
          >
            {isSaving ? 'Saving…' : getMessage('header.button_save_draft_logout')}
          </button>

          <button
            onClick={onLogoutWithoutSaving}
            disabled={isSaving || isDeleting}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface2)]',
              'text-[var(--text-dim)] text-[12px] cursor-pointer',
              'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.05)]',
              'transition-all duration-[180ms]',
              (isSaving || isDeleting) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isDeleting ? 'Cleaning up…' : getMessage('header.button_logout_without_saving')}
          </button>

          <button
            onClick={onCancel}
            disabled={isSaving || isDeleting}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
              'border border-[var(--border)] bg-[var(--surface2)]',
              'text-[var(--text-dim)] text-[12px] cursor-pointer',
              'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.05)]',
              'transition-all duration-[180ms]',
              (isSaving || isDeleting) && 'opacity-50 cursor-not-allowed'
            )}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  )
}
